import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import RoomSelection from './components/RoomSelection';
import Booking from './components/Booking';
import { BookingProvider } from './context/BookingContext';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import Member from './components/Member';
import { bookingScreenUrl, parseBookingLocation } from './utils/bookingUrl';

function App({ propertyEntry }) {
  const [currentPage, setCurrentPage] = useState(() => propertyEntry.entry.query.has('payment_status') ? 'index' : propertyEntry.entry.screen);
  const [paymentResult, setPaymentResult] = useState(() => {
    const query = propertyEntry.entry.query;
    return query.has('payment_status') ? {
      status: query.get('payment_status'), bookingReference: query.get('booking_reference'),
      reservationId: query.get('reservation_id'), message: query.get('message'),
    } : null;
  });
  useEffect(() => {
    // Input has already been captured by PropertyEntry; preserve the property path.
    const query = new URLSearchParams(window.location.search);
    propertyEntry.entry.query.forEach((value, key) => { if (!query.has(key)) query.set(key, value); });
    ['payment_status', 'booking_reference', 'reservation_id', 'message'].forEach(key => query.delete(key));
    if (query.get('from_widget') === 'true') {
      query.set('shop_id', String(propertyEntry.config.shop_id));
      query.set('shop_code', propertyEntry.config.shop_code);
      ['from_widget', 'hotel_name', 'client_url'].forEach(key => query.delete(key));
    }
    const search = query.toString();
    const screen = paymentResult ? 'index' : propertyEntry.entry.screen;
    const path = /^\/(booking|member)\/?$/.test(window.location.pathname) ? '/' : window.location.pathname;
    window.history.replaceState({}, '', path + (search ? '?' + search : '') + (screen === 'index' ? '' : '#/' + screen));
    window.dispatchEvent(new Event('bookingurlupdated'));
    const update = () => setCurrentPage(parseBookingLocation().screen);
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('hashchange', update);
    };
    // Entry data is immutable for the lifetime of this property screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const path = window.location.pathname + (currentPage === 'index' ? '' : '#/' + currentPage);
    if (window.dataLayer) window.dataLayer.push({ event: 'virtual_page_view', page_path: path, page_title: `Booking Engine - ${currentPage}` });
    if (typeof window.gtag === 'function') window.gtag('config', 'G-8EGP8LB2DZ', { page_path: path });
  }, [currentPage]);
  useEffect(() => {
    // Preserve the existing callback/conversion integration.
    if (paymentResult?.status === 'success') {
      const transactionId = paymentResult.reservationId || paymentResult.bookingReference;
      if (typeof window.gtag === 'function') window.gtag('event', 'conversion', {
        send_to: 'AW-11256234874/5XBdCILwgrsYEPqGsvcp', transaction_id: transactionId,
      });
      if (window.dataLayer) window.dataLayer.push({ event: 'payment_success_page', transaction_id: transactionId });
    }
  }, [paymentResult]);
  const navigateTo = page => {
    const screen = ['booking', 'member'].includes(page) ? page : 'index';
    window.history.pushState({}, '', bookingScreenUrl(screen));
    setCurrentPage(screen);
  };
  return (
    <BookingProvider initialState={propertyEntry.initialState} propertyConfig={propertyEntry.config} hotels={propertyEntry.hotels}>
      <div className="min-h-screen bg-white text-text-primary font-sans">
        <Header />
        <main>
          {currentPage === 'index' && <RoomSelection navigateTo={navigateTo} />}
          {currentPage === 'booking' && <Booking navigateTo={navigateTo} />}
          {currentPage === 'member' && <Member navigateTo={navigateTo} />}
        </main>
        {paymentResult && <PaymentSuccessModal result={paymentResult} onClose={() => setPaymentResult(null)} />}
      </div>
    </BookingProvider>
  );
}
export default App;
